import type { Choice, OddsSelection } from '@pons/contracts';
import { demoCoefficient, demoPrediction, type ArenaRound } from './model';

// Local demo only. All ledger amounts are integer hundredths of a point.
export const WELCOME_REWARD = 1000;
export const SHARE_REWARD = 250;
const FIRST_WEEK_REWARDS = [100, 125, 150, 175, 200, 225, 300];
export function dailyRewardForDay(streakDay: number) {
  if (!Number.isSafeInteger(streakDay) || streakDay < 1)
    throw new RangeError('Streak day must be a positive integer.');
  return FIRST_WEEK_REWARDS[streakDay - 1] ?? 300 + (streakDay - 7) * 25;
}
export const DAY = 86_400_000;
const number = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
export const formatPoints = (cents: number) => number.format(cents / 100);
export type Reward = 'welcome' | 'daily' | 'share';
export type Entry = {
  id: string;
  title: string;
  amountCents: number;
  day: number;
  status: string;
  detail?: string;
};
export type Position = {
  roundId: string;
  name: string;
  choice: Choice;
  stakeCents: number;
  coefficient: string;
  returnCents: number;
  status: 'pending' | 'won' | 'lost' | 'refunded';
};
export type PointsState = {
  connected: boolean;
  dayOffset: number;
  claims: number[];
  welcome: boolean;
  shared: boolean;
  entries: Entry[];
  positions: Record<string, Position>;
  message: string;
};
export type PointsAction =
  | { type: 'connect' | 'disconnect' | 'next-day' | 'reset' }
  | { type: 'claim'; reward: Reward; day: number }
  | { type: 'preset'; preset: string; day: number }
  | { type: 'settle'; rounds: ArenaRound[]; now: number; day: number };

export function initialPoints(): PointsState {
  return {
    connected: false,
    dayOffset: 0,
    claims: [],
    welcome: false,
    shared: false,
    entries: [],
    positions: {},
    message: '',
  };
}
export function pointsBalance(state: PointsState) {
  return state.entries.reduce((sum, entry) => sum + entry.amountCents, 0);
}
export function streakLength(claims: number[], today: number) {
  let day = claims.includes(today) ? today : today - 1;
  let streak = 0;
  while (claims.includes(day--)) streak++;
  return streak;
}
export function parsePointStake(input: string) {
  if (!/^(?:0|[1-9]\d{0,3})(?:\.\d{1,2})?$/.test(input)) return null;
  const [whole, fraction = ''] = input.split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return cents >= 100 && cents <= 100_000 ? cents : null;
}

export function placePointsPrediction(
  state: PointsState,
  round: ArenaRound,
  choice: Choice,
  now: number,
  selection: OddsSelection | undefined,
  day: number,
):
  | { ok: true; state: PointsState; round: ArenaRound }
  | { ok: false; error: string } {
  if (!state.connected)
    return {
      ok: false,
      error: 'Points are unavailable in this preview.',
    };
  if (choice !== 'DIE' && choice !== 'RUN')
    return { ok: false, error: 'Choose DIE or RUN.' };
  if (state.positions[round.id] || round.choice)
    return { ok: false, error: 'You already made a call on this token.' };
  const chosen = demoPrediction(round, choice, now);
  if (chosen === round || !chosen.demo)
    return { ok: false, error: 'The prediction window has closed.' };
  const stakeCents = selection ? parsePointStake(selection.amount) : null;
  if (stakeCents === null)
    return {
      ok: false,
      error: 'Choose a size from 1 to 1,000 PTS, with up to two decimals.',
    };
  const coefficient = demoCoefficient(
    choice === 'RUN' ? round.demo!.runOdds : round.demo!.dieOdds,
  );
  if (!coefficient || selection?.coefficient !== coefficient)
    return {
      ok: false,
      error: 'The coefficient changed. Check the new odds and click again.',
    };
  if (stakeCents > pointsBalance(state))
    return {
      ok: false,
      error: 'Not enough PTS. Lower your size or claim more points.',
    };
  const coefficientHundredths = Number(coefficient.replace('.', ''));
  const returnCents = Math.floor((stakeCents * coefficientHundredths) / 100);
  const position: Position = {
    roundId: round.id,
    name: round.name,
    choice,
    stakeCents,
    coefficient,
    returnCents,
    status: 'pending',
  };
  return {
    ok: true,
    round: {
      ...chosen,
      demo: {
        ...chosen.demo,
        stake: stakeCents / 100,
        lockedOdds: Number(coefficient),
        pointsFunded: true,
        pointsReturnCents: returnCents,
      },
    },
    state: {
      ...state,
      positions: { ...state.positions, [round.id]: position },
      entries: [
        {
          id: `prediction-${round.id}`,
          title: round.name,
          amountCents: -stakeCents,
          day,
          status: 'Placed',
          detail: `${choice === 'RUN' ? 'RUN' : 'DIE'} · ${coefficient}x`,
        },
        ...state.entries,
      ],
      message: `${formatPoints(stakeCents)} PTS placed at ${coefficient}x.`,
    },
  };
}

export function reducePoints(
  state: PointsState,
  action: PointsAction,
): PointsState {
  if (action.type === 'reset') return initialPoints();
  if (action.type === 'connect')
    return {
      ...state,
      connected: true,
      message: 'Your preview rewards are ready.',
    };
  if (action.type === 'disconnect')
    return {
      ...state,
      connected: false,
      message: 'Preview rewards unavailable.',
    };
  if (action.type === 'next-day')
    return {
      ...state,
      dayOffset: state.dayOffset + 1,
      message: 'Moved to the next demo day.',
    };
  if (action.type === 'preset') {
    let next = { ...initialPoints(), connected: action.preset !== 'new' };
    if (action.preset === 'claimed' || action.preset === 'streak') {
      next = reducePoints(next, {
        type: 'claim',
        reward: 'welcome',
        day: action.day - (action.preset === 'streak' ? 6 : 0),
      });
      const count = action.preset === 'streak' ? 6 : 1;
      for (let i = 0; i < count; i++)
        next = reducePoints(next, {
          type: 'claim',
          reward: 'daily',
          day: action.day - (action.preset === 'streak' ? 6 - i : 0),
        });
    }
    return { ...next, message: 'Preview state updated.' };
  }
  if (action.type === 'settle') {
    let next = state;
    for (const round of action.rounds) {
      const position = next.positions[round.id];
      if (!position || position.status !== 'pending') continue;
      const refund = round.canonical === false;
      if (!refund && (!round.outcome || action.now < round.settlesAt)) continue;
      const won = round.outcome === position.choice;
      const amountCents = refund
        ? position.stakeCents
        : won
          ? position.returnCents
          : 0;
      const status = refund ? 'refunded' : won ? 'won' : 'lost';
      next = {
        ...next,
        positions: { ...next.positions, [round.id]: { ...position, status } },
        entries: [
          {
            id: `settlement-${round.id}`,
            title: position.name,
            amountCents,
            day: action.day,
            status: refund ? 'Refunded' : won ? 'Won' : 'Lost',
            detail: `${position.choice === 'RUN' ? 'RUN' : 'DIE'} · ${position.coefficient}x`,
          },
          ...next.entries,
        ],
        message: refund
          ? `${formatPoints(amountCents)} PTS refunded.`
          : won
            ? `You won ${formatPoints(amountCents)} PTS, including your prediction size.`
            : 'Call settled. No payout this time.',
      };
    }
    return next;
  }
  if (action.type !== 'claim' || !state.connected) return state;
  const { reward, day } = action;
  if (
    (reward === 'welcome' && state.welcome) ||
    (reward === 'share' && state.shared) ||
    (reward === 'daily' && state.claims.includes(day))
  )
    return state;
  const amount =
    reward === 'welcome'
      ? WELCOME_REWARD
      : reward === 'share'
        ? SHARE_REWARD
        : dailyRewardForDay(streakLength(state.claims, day) + 1);
  const title =
    reward === 'welcome'
      ? 'Welcome reward'
      : reward === 'share'
        ? 'Share on X'
        : 'Daily check-in';
  return {
    ...state,
    welcome: state.welcome || reward === 'welcome',
    shared: state.shared || reward === 'share',
    claims: reward === 'daily' ? [...state.claims, day] : state.claims,
    entries: [
      {
        id: `${reward}-${day}`,
        title,
        amountCents: amount * 100,
        day,
        status: 'Claimed',
      },
      ...state.entries,
    ],
    message: `+${number.format(amount)} demo PTS claimed.`,
  };
}
